import React, { useEffect, useState } from 'react';
import { KPI_SECTIONS, PERIOD_OPTIONS } from '../constants/kpiSections';
import './KpiGateModal.css';

/**
 * KpiGateModal
 *
 * Full-page onboarding gate shown over the Chat workspace whenever the
 * user's KPI profile isn't complete yet — this is the "initial KPI
 * collection" step that happens right after signup/login, before the
 * chat experience becomes the primary focus of the page.
 *
 * Dismissible via "Skip for now" (the user can always finish later from
 * Settings → KPI's), but reappears on the next visit until completed —
 * see Profile.jsx, which re-derives `showGate` from the fetched profile
 * every time the workspace mounts.
 */
const KpiGateModal = ({ profile, onSaveSection, onFinish, onSkip }) => {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [period, setPeriod] = useState('month');
  const [saving, setSaving] = useState(false);
  const [sectionError, setSectionError] = useState('');

  const section = KPI_SECTIONS[step];
  const sectionData = profile?.[section.key] || {};

  useEffect(() => {
    const initial = {};
    section.fields.forEach((f) => { initial[f.name] = sectionData[f.name] ?? ''; });
    setValues(initial);
    setPeriod(sectionData.period || section.period?.defaultValue || 'month');
    setSectionError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleChange = (name) => (e) => {
    setValues((v) => ({ ...v, [name]: e.target.value }));
  };

  const parsedValues = () => {
    const out = {};
    for (const f of section.fields) {
      const raw = values[f.name];
      if (raw === '' || raw === null || raw === undefined) { out[f.name] = null; continue; }
      const num = Number(raw);
      if (Number.isNaN(num)) return null;
      out[f.name] = num;
    }
    if (section.period) out.period = period;
    return out;
  };

  const isLastStep = step === KPI_SECTIONS.length - 1;

  const handleNext = async () => {
    const payload = parsedValues();
    if (!payload) {
      setSectionError('Please enter numbers only.');
      return;
    }
    setSaving(true);
    setSectionError('');
    try {
      const updated = await onSaveSection(section.key, payload);
      if (isLastStep) {
        onFinish(updated);
      } else {
        setStep((s) => s + 1);
      }
    } catch (err) {
      setSectionError(err.message || 'Could not save — try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kpi-gate" role="dialog" aria-modal="true" aria-label="KPI onboarding">
      <div className="kpi-gate__backdrop" />
      <div className="kpi-gate__card glass-card">
        <div className="kpi-gate__header">
          <span className="kpi-gate__eyebrow">Step {step + 1} of {KPI_SECTIONS.length}</span>
          <h1 className="kpi-gate__title">Let's set up your financial picture.</h1>
          <p className="kpi-gate__sub">
            Five numbers, a couple of minutes — this configures every recommendation the assistant gives you.
          </p>
        </div>

        <div className="kpi-gate__progress-track">
          <div className="kpi-gate__progress-fill" style={{ width: `${((step) / KPI_SECTIONS.length) * 100}%` }} />
        </div>

        <div className="kpi-gate__dots">
          {KPI_SECTIONS.map((s, i) => (
            <span
              key={s.key}
              className={`kpi-gate__dot${i === step ? ' kpi-gate__dot--active' : ''} kpi-gate__dot--${profile?.[s.key]?.status || 'not_started'}`}
              title={s.label}
            />
          ))}
        </div>

        <div className="kpi-gate__section">
          <h2 className="kpi-gate__section-title">{section.label}</h2>
          <p className="kpi-gate__section-desc">{section.description}</p>

          {section.period && (
            <label className="kpi-gate__period">
              <span className="kpi-gate__field-label">These figures are reported</span>
              <select
                className="kpi-gate__period-select"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                {PERIOD_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>
          )}

          <div className="kpi-gate__fields">
            {section.fields.map((f) => (
              <label key={f.name} className="kpi-gate__field">
                <span className="kpi-gate__field-label">{f.label}</span>
                <div className="kpi-gate__field-input-wrap">
                  {f.prefix && <span className="kpi-gate__field-prefix">{f.prefix}</span>}
                  <input
                    type="number"
                    inputMode="decimal"
                    className="kpi-gate__field-input"
                    value={values[f.name] ?? ''}
                    onChange={handleChange(f.name)}
                    placeholder="0"
                    autoFocus={f === section.fields[0]}
                  />
                </div>
              </label>
            ))}
          </div>

          {sectionError && <p className="kpi-gate__error">{sectionError}</p>}
        </div>

        <div className="kpi-gate__nav">
          {step > 0 && (
            <button className="btn btn--sm btn--ghost" onClick={() => setStep((s) => s - 1)} disabled={saving}>
              Back
            </button>
          )}
          <button className="btn btn--sm btn--gold kpi-gate__next" onClick={handleNext} disabled={saving}>
            {saving ? 'Saving…' : isLastStep ? 'Finish' : 'Next'}
          </button>
        </div>

        <button className="kpi-gate__skip" onClick={onSkip}>
          Skip for now — I'll finish this later in Settings
        </button>
      </div>
    </div>
  );
};

export default KpiGateModal;
